import React, { useMemo, useState } from "react";
import "./LocationsManagement.css";
import Modal from "./Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { createLocation, updateLocation, deleteLocation } from "../utils/api"; // Ensure deleteLocation is in api.js
import PageHeader from "./layout/PageHeader";
import { MapPin } from "lucide-react";

const LocationsManagement = ({
  locations,
  setLocations,
  currentActiveLocation,
  setCurrentActiveLocation,
  activeLocation,
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const supportedTimezones = useMemo(() => {
    try {
      const values = Intl.supportedValuesOf("timeZone");
      return Array.isArray(values) ? values : [];
    } catch {
      return [];
    }
  }, []);

  const getOffsetLabel = (tz) => {
    try {
      const offset =
        new Intl.DateTimeFormat("en", {
          timeZone: tz,
          timeZoneName: "shortOffset",
        })
          .formatToParts()
          .find((p) => p.type === "timeZoneName")?.value || "";
      return offset.replace("GMT", "UTC");
    } catch {
      return "UTC";
    }
  };

  const getFriendlyLabel = (tz) => {
    try {
      return (
        new Intl.DateTimeFormat("en", {
          timeZone: tz,
          timeZoneName: "long",
        })
          .formatToParts()
          .find((p) => p.type === "timeZoneName")?.value || tz
      );
    } catch {
      return tz;
    }
  };

  const formatTimezoneDisplay = (tz) => `${tz} — ${getFriendlyLabel(tz)} (${getOffsetLabel(tz)})`;

  const getTimezoneGroup = (tz) => {
    if (tz.startsWith("America/")) return "Americas";
    if (tz.startsWith("Europe/")) return "Europe";
    if (tz.startsWith("Asia/")) return "Asia";
    if (tz.startsWith("Africa/")) return "Africa";
    if (tz.startsWith("Pacific/") || tz.startsWith("Australia/")) return "Pacific";
    return "Other";
  };

  const highlightMatch = (text, query) => {
    const q = String(query || "").trim();
    if (!q) return text;
    const lowerText = text.toLowerCase();
    const lowerQ = q.toLowerCase();
    const idx = lowerText.indexOf(lowerQ);
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <span className="timezone-highlight">{text.slice(idx, idx + q.length)}</span>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationToDelete, setLocationToDelete] = useState(null);

  // State for Add New Location form
  const [newLocationName, setNewLocationName] = useState("");
  const [newStreetAddress, setNewStreetAddress] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("");
  const [newZipCode, setNewZipCode] = useState("");
  const [timezoneQuery, setTimezoneQuery] = useState("");
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [timezoneError, setTimezoneError] = useState("");
  const [newLocationTimezone, setNewLocationTimezone] = useState("America/New_York");
  const [newTimezoneQuery, setNewTimezoneQuery] = useState("");
  const [newTimezoneOpen, setNewTimezoneOpen] = useState(false);
  const [newTimezoneError, setNewTimezoneError] = useState("");

  // Load locations from API on mount (or when currentActiveLocation changes if needed, but usually App.js handles initial load)
  // Since App.js passes 'locations', we might rely on that, but for CRUD we should update the backend and then local state.

  // NOTE: Ideally App.js should manage the fetching and passing down of locations if it's global state.
  // However, for this component to be self-contained for CRUD, we can perform API calls here and then update the parent state via setLocations.

  const inferTimezoneFromState = (state) => {
    const s = String(state || "").trim().toLowerCase();
    if (s === "new york" || s === "ny") return "America/New_York";
    if (s === "california" || s === "ca") return "America/Los_Angeles";
    if (s === "texas" || s === "tx") return "America/Chicago";
    if (s === "florida" || s === "fl") return "America/New_York";
    return "America/New_York";
  };

  const handleOpenAddModal = () => {
    const defaultTz = "America/New_York";
    setNewLocationTimezone(defaultTz);
    setNewTimezoneQuery(formatTimezoneDisplay(defaultTz));
    setNewTimezoneError("");
    setNewTimezoneOpen(false);
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    // Reset form fields
    setNewLocationName("");
    setNewStreetAddress("");
    setNewCity("");
    setNewState("");
    setNewZipCode("");
    setNewLocationTimezone("America/New_York");
    setNewTimezoneQuery("");
    setNewTimezoneError("");
  };

  const handleCreateLocation = async (e) => {
    e?.preventDefault?.();
    if (!String(newLocationName || "").trim()) {
      alert("Location name is required.");
      return;
    }
    const tz =
      String(newLocationTimezone || "").trim() ||
      inferTimezoneFromState(newState);
    if (!tz) {
      setNewTimezoneError("Please set an office timezone before creating");
      return;
    }

    const newLocationData = {
      name: newLocationName.trim(),
      address: newStreetAddress,
      city: newCity,
      state: newState,
      zip: newZipCode,
      timezone: tz,
      status: "Active",
    };

    try {
      const createdLocation = await createLocation(newLocationData);
      // Update local state (and thus App.js state)
      // If this is the first location (user had none before), make it active immediately
      if (!currentActiveLocation && locations.length === 0) {
        const activeLoc = { ...createdLocation, isCurrent: true };
        setCurrentActiveLocation(activeLoc);
        localStorage.setItem("activeLocation", JSON.stringify(createdLocation));
        setLocations((prevData) => [...prevData, activeLoc]);
      } else {
        setLocations((prevData) => [
          ...prevData,
          { ...createdLocation, isCurrent: false },
        ]);
      }

      handleCloseAddModal();
    } catch (error) {
      console.error("Failed to create location:", error);
      const detail =
        error?.response?.data?.message ||
        error?.message ||
        "Please try again.";
      alert(`Failed to create location. ${detail}`);
    }
  };

  const handleOpenEditModal = (location) => {
    setCurrentLocation(location);
    setTimezoneQuery(
      location?.timezone ? formatTimezoneDisplay(location.timezone) : "",
    );
    setTimezoneError("");
    setTimezoneOpen(false);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentLocation(null);
    setTimezoneQuery("");
    setTimezoneError("");
    setTimezoneOpen(false);
  };

  const handleUpdateLocation = async () => {
    try {
      if (!String(currentLocation?.timezone || "").trim()) {
        setTimezoneError("Please set an office timezone before saving");
        return;
      }
      const updatedData = {
        name: currentLocation.name,
        address: currentLocation.address,
        city: currentLocation.city,
        state: currentLocation.state,
        zip: currentLocation.zip,
        status: currentLocation.status,
        aiHiringEnabled: !!currentLocation.aiHiringEnabled,
        timezone: currentLocation.timezone,
      };

      const updatedLocation = await updateLocation(
        currentLocation.id,
        updatedData,
      );

      setLocations((prevData) =>
        prevData.map((location) =>
          location.id === currentLocation.id
            ? { ...updatedLocation, isCurrent: location.isCurrent } // Preserve isCurrent flag
            : location,
        ),
      );

      // If we updated the currently active location, update that state too
      if (
        currentActiveLocation &&
        currentActiveLocation.id === currentLocation.id
      ) {
        setCurrentActiveLocation({ ...updatedLocation, isCurrent: true });
      }

      handleCloseEditModal();
    } catch (error) {
      console.error("Failed to update location:", error);
      alert("Failed to update location.");
    }
  };

  const handleOpenDeleteConfirmModal = (location) => {
    setLocationToDelete(location);
    setIsDeleteConfirmModalOpen(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmModalOpen(false);
    setLocationToDelete(null);
  };

  const handleDeleteLocation = async () => {
    console.log(`Delete location with ID: ${locationToDelete.id}`);

    try {
      await deleteLocation(locationToDelete.id);

      setLocations((prevData) =>
        prevData.filter((loc) => loc.id !== locationToDelete.id),
      );

      // If the deleted location was the current active location, reset active location in App.js
      if (
        currentActiveLocation &&
        locationToDelete.id === currentActiveLocation.id
      ) {
        const remainingLocations = locations.filter(
          (loc) => loc.id !== locationToDelete.id,
        );
        if (remainingLocations.length > 0) {
          const nextActive = { ...remainingLocations[0], isCurrent: true };
          setCurrentActiveLocation(nextActive);
          setLocations((prev) =>
            prev.map((loc, index) =>
              index === 0
                ? { ...loc, isCurrent: true }
                : { ...loc, isCurrent: false },
            ),
          );
        } else {
          setCurrentActiveLocation(null);
        }
      }
      handleCloseDeleteConfirmModal();
    } catch (error) {
      console.error("Failed to delete location:", error);
      alert("Failed to delete location.");
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentLocation((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const buildTimezoneGroups = (queryRaw) => {
    const query = String(queryRaw || "").trim().toLowerCase();
    const filtered = supportedTimezones.filter((tz) => {
      if (!query) return true;
      const label = formatTimezoneDisplay(tz).toLowerCase();
      return label.includes(query);
    });
    const grouped = {
      Americas: [],
      Europe: [],
      Asia: [],
      Africa: [],
      Pacific: [],
      Other: [],
    };
    filtered.forEach((tz) => {
      grouped[getTimezoneGroup(tz)].push(tz);
    });
    return grouped;
  };

  const filteredTimezoneGroups = useMemo(
    () => buildTimezoneGroups(timezoneQuery),
    [supportedTimezones, timezoneQuery],
  );

  const filteredNewTimezoneGroups = useMemo(
    () => buildTimezoneGroups(newTimezoneQuery),
    [supportedTimezones, newTimezoneQuery],
  );

  return (
    <div className="locations-management-container page-shell">
      <PageHeader
        title="Locations"
        subtitle="Manage service locations and keep records current."
        icon={<MapPin className="h-6 w-6 text-white" />}
        activeLocation={activeLocation || currentActiveLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      <div className="page-actions">
        <button
          className="button add-location-button"
          onClick={handleOpenAddModal}
        >
          + Add Location
        </button>
      </div>
      <div className="locations-table-container">
        <table>
          <thead>
            <tr>
              <th>Location</th>
              <th>Address</th>
              <th>Clients</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((location) => (
              <tr key={location.id}>
                <td>
                  <span className="location-name">
                    {location.name}
                    {currentActiveLocation &&
                      (location.id || location._id) &&
                      (currentActiveLocation.id || currentActiveLocation._id) &&
                      (location.id || location._id) ===
                        (currentActiveLocation.id ||
                          currentActiveLocation._id) && (
                        <span className="selected-tag">Selected</span>
                      )}
                  </span>
                  <br />
                  <span className="location-sub-address">
                    {[location.city, location.state].filter(Boolean).join(", ")}
                  </span>
                </td>
                <td>
                  {[
                    location.address,
                    location.city,
                    location.state,
                    location.zip,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </td>
                <td>
                  <span className="status-badge neutral">
                    {typeof location.clientCount === "number"
                      ? `${location.clientCount} client${
                          location.clientCount === 1 ? "" : "s"
                        }`
                      : "0 clients"}
                  </span>
                </td>
                <td>
                  <span
                    className={`status-badge ${
                      location.status?.toLowerCase() || ""
                    }`}
                  >
                    {location.status}
                  </span>
                </td>
                <td>
                  <button
                    className="action-button edit-button"
                    onClick={() => handleOpenEditModal(location)}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    className="action-button delete-button"
                    onClick={() => handleOpenDeleteConfirmModal(location)}
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add New Location Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        title="Add New Location"
      >
        <form>
          <div className="form-group">
            <label>Location Name *</label>
            <input
              type="text"
              name="name"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
            />
          </div>
          <div className="form-section-title">
            <h3>Address Information</h3>
          </div>
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              name="address"
              value={newStreetAddress}
              onChange={(e) => setNewStreetAddress(e.target.value)}
            />
          </div>
          <div className="form-group-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={newCity}
                onChange={(e) => setNewCity(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                name="state"
                value={newState}
                onChange={(e) => setNewState(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>ZIP Code *</label>
              <input
                type="text"
                name="zip"
                value={newZipCode}
                onChange={(e) => setNewZipCode(e.target.value)}
              />
            </div>
          </div>
          <div className="form-group timezone-field">
            <label>Office Timezone *</label>
            <div className="timezone-combobox-wrap">
              <input
                type="text"
                value={newTimezoneQuery}
                placeholder="Search timezones..."
                onFocus={() => setNewTimezoneOpen(true)}
                onBlur={() => {
                  setTimeout(() => setNewTimezoneOpen(false), 150);
                }}
                onChange={(e) => {
                  setNewTimezoneQuery(e.target.value);
                  setNewTimezoneOpen(true);
                  setNewTimezoneError("");
                }}
              />
              {newTimezoneOpen && (
                <div className="timezone-dropdown">
                  {Object.entries(filteredNewTimezoneGroups).map(([group, values]) =>
                    values.length ? (
                      <div key={group} className="timezone-group">
                        <div className="timezone-group-header">{group}</div>
                        {values.map((tz) => {
                          const label = formatTimezoneDisplay(tz);
                          return (
                            <button
                              type="button"
                              key={tz}
                              className="timezone-option"
                              onMouseDown={() => {
                                setNewLocationTimezone(tz);
                                setNewTimezoneQuery(label);
                                setNewTimezoneError("");
                                setNewTimezoneOpen(false);
                              }}
                            >
                              {highlightMatch(label, newTimezoneQuery)}
                            </button>
                          );
                        })}
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </div>
            {newTimezoneError ? (
              <div className="form-inline-error">{newTimezoneError}</div>
            ) : null}
            <div className="form-helper-text">
              Used to schedule caregiver orientations at the correct local time.
            </div>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseAddModal}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="button create-button"
              onClick={handleCreateLocation}
            >
              CREATE
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Location Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Location"
      >
        <form>
          <div className="form-group">
            <label>Location Name *</label>
            <input
              type="text"
              name="name"
              value={currentLocation?.name || ""}
              onChange={handleEditFormChange}
            />
          </div>
          <div className="form-section-title">
            <h3>Address Information</h3>
          </div>
          <div className="form-group">
            <label>Street Address *</label>
            <input
              type="text"
              name="address"
              value={currentLocation?.address || ""}
              onChange={handleEditFormChange}
            />
          </div>
          <div className="form-group-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                name="city"
                value={currentLocation?.city || ""}
                onChange={handleEditFormChange}
              />
            </div>
            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                name="state"
                value={currentLocation?.state || ""}
                onChange={handleEditFormChange}
              />
            </div>
            <div className="form-group">
              <label>ZIP Code *</label>
              <input
                type="text"
                name="zip"
                value={currentLocation?.zip || ""}
                onChange={handleEditFormChange}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Status *</label>
            <select
              name="status"
              value={currentLocation?.status || ""}
              onChange={handleEditFormChange}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
              <option value="Pending">Pending</option>
            </select>
          </div>
          <div className="form-group timezone-field">
            <label>Office Timezone *</label>
            <div className="timezone-combobox-wrap">
              <input
                type="text"
                value={timezoneQuery}
                placeholder="Search timezones..."
                onFocus={() => setTimezoneOpen(true)}
                onBlur={() => {
                  setTimeout(() => setTimezoneOpen(false), 150);
                }}
                onChange={(e) => {
                  setTimezoneQuery(e.target.value);
                  setTimezoneOpen(true);
                  setTimezoneError("");
                }}
              />
              {timezoneOpen && (
                <div className="timezone-dropdown">
                  {Object.entries(filteredTimezoneGroups).map(([group, values]) =>
                    values.length ? (
                      <div key={group} className="timezone-group">
                        <div className="timezone-group-header">{group}</div>
                        {values.map((tz) => {
                          const label = formatTimezoneDisplay(tz);
                          return (
                            <button
                              type="button"
                              key={tz}
                              className="timezone-option"
                              onMouseDown={() => {
                                setCurrentLocation((prev) => ({
                                  ...prev,
                                  timezone: tz,
                                }));
                                setTimezoneQuery(label);
                                setTimezoneError("");
                                setTimezoneOpen(false);
                              }}
                            >
                              {highlightMatch(label, timezoneQuery)}
                            </button>
                          );
                        })}
                      </div>
                    ) : null,
                  )}
                </div>
              )}
            </div>
            {timezoneError ? (
              <div className="form-inline-error">{timezoneError}</div>
            ) : null}
            <div className="form-helper-text">
              Used to schedule caregiver orientations at the correct local time.
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-label">
              <span>Enable AI Hiring for this agency</span>
              <input
                type="checkbox"
                name="aiHiringEnabled"
                checked={!!currentLocation?.aiHiringEnabled}
                onChange={handleEditFormChange}
              />
            </label>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseEditModal}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="button update-button"
              onClick={handleUpdateLocation}
            >
              UPDATE
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Location Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmModalOpen}
        onClose={handleCloseDeleteConfirmModal}
        title="Delete Location"
      >
        <div className="delete-confirmation-content">
          <div className="warning-icon">!</div>
          <p>
            Are you sure you want to delete the location "
            <strong>{locationToDelete?.name}</strong>"?
          </p>
          <p>
            The location will be marked for deletion, but all files and folders
            will be retained for 2 months. If this action was taken by mistake,
            please contact the administrator to request a recovery within this
            period.
          </p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="button cancel-button"
            onClick={handleCloseDeleteConfirmModal}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="button delete-confirm-button"
            onClick={handleDeleteLocation}
          >
            DELETE LOCATION
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default LocationsManagement;
